import { InputHTMLAttributes, forwardRef, useId } from "react";
import { focusRing } from "@ui/tokens/interaction";

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  className?: string;
}

/**
 * The ref is forwarded so a modal can hand its native input to Headless UI's `Dialog` as the
 * element to focus on open — see Modal's `initialFocus`.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, className = "", id, ...props },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const errorId = `${inputId}-error`;

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      {label && (
        <label htmlFor={inputId} className="text-field-label uppercase text-ink-help">
          {label}
        </label>
      )}
      <input
        ref={ref}
        id={inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        className={`w-full min-h-touch bg-surface-raised rounded-field app:rounded-pill border px-3.25 app:px-5 py-3 app:bg-[var(--field-surface,var(--well))] app:shadow-field app:disabled:border-transparent app:text-ink app:placeholder:text-placeholder app:focus-visible:shadow-input-focus font-ui text-body text-ink placeholder:text-ink-meta disabled:bg-disabled-bg disabled:text-disabled-fg disabled:cursor-not-allowed ${
          error ? "border-danger-line app:border-field-edge-error" : "border-line-field app:border-field-edge"
        } ${focusRing}`}
        {...props}
      />
      {error && (
        <span id={errorId} className="text-body text-danger">
          {error}
        </span>
      )}
    </div>
  );
});